import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import apiService from "../../services/api";
import { BulkEmailStats, BulkEmailHistory } from "../../types";

interface EmailStatsState {
  stats: BulkEmailStats | null;
  loading: boolean;
  error: string | null;
  history: BulkEmailHistory[];
  historyLoading: boolean;
  historyError: string | null;
  historyTotalPages: number;
  historyCurrentPage: number;
}

const initialState: EmailStatsState = {
  stats: null,
  loading: false,
  error: null,
  history: [],
  historyLoading: false,
  historyError: null,
  historyTotalPages: 0,
  historyCurrentPage: 1,
};

// Async thunks
export const fetchStats = createAsyncThunk(
  "emailStats/fetchStats",
  async (filters: any, { rejectWithValue }) => {
    try {
      const response = await apiService.getEmailStats(filters);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch stats");
    }
  }
);

export const fetchEmailHistory = createAsyncThunk(
  "emailStats/fetchEmailHistory",
  async (
    params: {
      page?: number;
      limit?: number;
      startDate?: string;
      endDate?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiService.getEmailHistory(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch email history");
    }
  }
);

export const sendBulkEmail = createAsyncThunk(
  "emailStats/sendBulkEmail",
  async (
    emailData: {
      recipients: string[];
      subject: string;
      message: string;
      templateId?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiService.sendBulkEmail(emailData);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to send email");
    }
  }
);

const emailStatsSlice = createSlice({
  name: "emailStats",
  initialState,
  reducers: {
    clearStatsError: (state) => {
      state.error = null;
    },
    clearHistoryError: (state) => {
      state.historyError = null;
    },
    setHistoryPage: (state, action: PayloadAction<number>) => {
      state.historyCurrentPage = action.payload;
    },
    clearStats: (state) => {
      state.stats = null;
      state.error = null;
    },
    clearHistory: (state) => {
      state.history = [];
      state.historyError = null;
      state.historyCurrentPage = 1;
    },
  },
  extraReducers: (builder) => {
    // Fetch stats
    builder
      .addCase(fetchStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload;
      })
      .addCase(fetchStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch email history
    builder
      .addCase(fetchEmailHistory.pending, (state) => {
        state.historyLoading = true;
        state.historyError = null;
      })
      .addCase(fetchEmailHistory.fulfilled, (state, action) => {
        state.historyLoading = false;
        state.history = action.payload.history || action.payload;
        state.historyTotalPages = action.payload.totalPages || 1;
      })
      .addCase(fetchEmailHistory.rejected, (state, action) => {
        state.historyLoading = false;
        state.historyError = action.payload as string;
      });

    // Send bulk email
    builder
      .addCase(sendBulkEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendBulkEmail.fulfilled, (state, action) => {
        state.loading = false;
        // Removed state.lastSendResult assignment because lastSendResult does not exist on EmailStatsState
      })
      .addCase(sendBulkEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearStatsError,
  clearHistoryError,
  setHistoryPage,
  clearStats,
  clearHistory,
} = emailStatsSlice.actions;

export default emailStatsSlice.reducer;
